import { reflect } from '@effector/reflect';
import {
	ActionIcon,
	Checkbox,
	Group,
	PasswordInput,
	Select,
	Stack,
	TextInput,
	Tooltip,
} from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus, RefreshCw, Copy } from 'lucide-react';
import { not } from 'patronum';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import { viewerModel } from 'entities/viewer';
import { authModel, USER_ROLES, UserRole } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm } from 'shared/ui';
import { AddUserModel } from '../../model';
import cls from './add-user-modal.module.scss';

type CopyableReadOnlyFieldViewProps = {
	value: string;
	label: string;
	copyTooltipLabel: string;
	inputTestId: string;
	copyButtonTestId: string;
	onCopy: () => void;
	variant: 'text' | 'password';
};

const CopyableReadOnlyFieldView = ({
	value,
	label,
	copyTooltipLabel,
	inputTestId,
	copyButtonTestId,
	onCopy,
	variant,
}: CopyableReadOnlyFieldViewProps) => {
	const Input = variant === 'password' ? PasswordInput : TextInput;

	return (
		<Group
			align='flex-end'
			gap='xs'
			wrap='nowrap'
		>
			<Input
				readOnly
				flex={1}
				className={cls.passwordField}
				data-test-id={inputTestId}
				label={label}
				value={value}
			/>
			<Tooltip label={copyTooltipLabel}>
				<ActionIcon
					variant='ghost-secondary'
					size='md'
					data-test-id={copyButtonTestId}
					aria-label={copyTooltipLabel}
					onClick={onCopy}
				>
					<Copy />
				</ActionIcon>
			</Tooltip>
		</Group>
	);
};

type PasswordFieldViewProps = {
	value: string;
	label: string;
	placeholder: string;
	generateTooltipLabel: string;
	error: string | false;
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
	onGenerate: () => void;
};

const PasswordFieldView = ({
	value,
	label,
	placeholder,
	generateTooltipLabel,
	error,
	onChange,
	onGenerate,
}: PasswordFieldViewProps) => (
	<Group
		align='flex-end'
		gap='xs'
		wrap='nowrap'
	>
		<PasswordInput
			flex={1}
			className={cls.passwordField}
			data-test-id='add-user-password-input'
			label={label}
			placeholder={placeholder}
			errorProps={{ 'data-test-id': 'add-user-password-input-error' }}
			error={error}
			value={value}
			onChange={onChange}
		/>
		<Tooltip label={generateTooltipLabel}>
			<ActionIcon
				variant='ghost-secondary'
				size='md'
				data-test-id='add-user-generate-password-button'
				aria-label={generateTooltipLabel}
				onClick={onGenerate}
			>
				<RefreshCw />
			</ActionIcon>
		</Tooltip>
	</Group>
);

export const createAddUserModal = (model: AddUserModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-user-name-input',
			label: translated('add-user.name'),
			placeholder: translated('add-user.name'),
			errorProps: { 'data-test-id': 'add-user-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.name'),
						minLength: USER_NAME_MIN_LENGTH,
						maxLength: USER_NAME_MAX_LENGTH,
					}),
			),
			value: model.form.fields.name.$value,
			onChange: (e) => model.form.fields.name.change(e.target.value),
		},
	});

	const PasswordField = reflect({
		view: PasswordFieldView,
		bind: {
			label: translated('add-user.password'),
			placeholder: translated('add-user.password'),
			generateTooltipLabel: translated('add-user.generate-password'),
			error: combine(
				[model.form.fields.password.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.password'),
						minLength: USER_PASSWORD_MIN_LENGTH,
					}),
			),
			value: model.form.fields.password.$value,
			onChange: (e) => model.form.fields.password.change(e.target.value),
			onGenerate: model.passwordGenerateClicked,
		},
	});

	const AccountField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-user-account-select',
			value: model.form.fields.accountId.$value.map((value) => value?.toString() ?? null),
			onChange: (value) =>
				model.form.fields.accountId.change(value === null ? value : Number(value)),
			data: model.$accounts.map((accounts) =>
				accounts.map(({ id, name }) => ({
					value: id.toString(),
					label: name,
				})),
			),
			disabled: model.form.fields.accountId.$disabled,
			label: translated('add-user.account'),
			placeholder: translated('add-user.account'),
			errorProps: { 'data-test-id': 'add-user-account-select-error' },
			error: combine(
				[model.form.fields.accountId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.account'),
					}),
			),
			searchable: true,
		},
	});

	const RoleField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-user-role-select',
			label: translated('add-user.role'),
			placeholder: translated('add-user.role'),
			value: model.form.fields.role.$value.map((value) => value?.toString() ?? null),
			onChange: (value) =>
				model.form.fields.role.change(value === null ? value : (Number(value) as UserRole)),
			data: combine([
				$t,
				authModel.$role,
				model.form.fields.accountId.$value,
				viewerModel.$accountId,
			]).map(([t, role, targetAccountId, accountId]) =>
				Object.values(USER_ROLES)
					.filter((r) => {
						const isSameAccount = targetAccountId === accountId;
						const isGod = role === USER_ROLES.god;

						return isGod && isSameAccount ? true : r !== USER_ROLES.god;
					})
					.map((r) => ({
						value: `${r}`,
						label: t(`user-roles.${r}`),
					})),
			),
			errorProps: { 'data-test-id': 'add-user-role-select-error' },
			error: combine(
				[model.form.fields.role.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.role'),
					}),
			),
		},
	});

	const NoteField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-user-note-input',
			label: translated('add-user.note'),
			placeholder: translated('add-user.note'),
			errorProps: { 'data-test-id': 'add-user-note-input-error' },
			error: combine(
				[model.form.fields.note.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.note'),
						maxLength: USER_NOTE_MAX_LENGTH,
					}),
			),
			value: model.form.fields.note.$value,
			onChange: (e) => model.form.fields.note.change(e.target.value),
		},
	});

	const MultiLoginField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-user-allow-multi-login-checkbox',
			label: translated('add-user.allow-multi-login'),
			disabled: model.form.fields.hasMultiLogin.$disabled,
			checked: model.form.fields.hasMultiLogin.$value,
			onChange: (e) => model.form.fields.hasMultiLogin.change(e.target.checked),
		},
	});

	const AddUserModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-user-button',
			leftSection: <Plus />,
			className: cls.addUserModalButton,
			disabled: not(viewerModel.$canWriteUsers),
			children: translated('add-user.label'),
			modalData: null,
		},
	});

	const SuccessUsernameField = reflect({
		view: CopyableReadOnlyFieldView,
		bind: {
			variant: 'text' as const,
			value: model.$createdCredentials.map((credentials) => credentials?.name ?? ''),
			label: translated('add-user.name'),
			copyTooltipLabel: translated('add-user.copy-username'),
			inputTestId: 'add-user-success-name-input',
			copyButtonTestId: 'add-user-copy-username-button',
			onCopy: model.usernameCopyClicked,
		},
	});

	const SuccessPasswordField = reflect({
		view: CopyableReadOnlyFieldView,
		bind: {
			variant: 'password' as const,
			value: model.$createdCredentials.map((credentials) => credentials?.password ?? ''),
			label: translated('add-user.password'),
			copyTooltipLabel: translated('add-user.copy-password'),
			inputTestId: 'add-user-success-password-input',
			copyButtonTestId: 'add-user-copy-password-button',
			onCopy: model.passwordCopyClicked,
		},
	});

	const AddUserModal = () => {
		const { t } = useTranslation();

		const [pending, closed, createdCredentials] = useUnit([
			model.$pending,
			model.modalClosed,
			model.$createdCredentials,
		]);

		const isSuccess = createdCredentials !== null;

		return (
			<ModalForm
				title={t(isSuccess ? 'add-user.success-title' : 'add-user.title')}
				cancelButtonProps={{
					children: t(isSuccess ? 'add-user.success-cancel' : 'add-user.cancel'),
				}}
				submitButtonProps={{
					children: isSuccess ? t('add-user.success-submit') : t('add-user.add'),
					loading: pending,
				}}
				onExitTransitionEnd={closed}
			>
				{isSuccess ? (
					<Stack gap='md'>
						<SuccessUsernameField />
						<SuccessPasswordField />
					</Stack>
				) : (
					<>
						<NameField />
						<PasswordField />
						<AccountField />
						<RoleField />
						<NoteField />
						<MultiLoginField />
					</>
				)}
			</ModalForm>
		);
	};

	return Object.assign(AddUserModal, {
		Button: AddUserModalButton,
	});
};
